import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { NoticeSession } from '@models/planning/notice-session.model'
import { NoticeSessionPhaseBid, NoticeSessionPhaseBidSearch } from '@models/planning/notice-session-phase-bid.model'
import { NoticeSessionPhaseBidService } from '@services/notice-session-phase-bid.service'
import { AccountService } from '@services/account.service'
import {
	ChatMemberStatus,
	ChatMessage,
	ChatMessagePayload,
	ChatMessageType,
	ChatPresence,
	ChatRoom,
	ChatRoomStatus,
	ChatWsEvent,
	ChatParticipant,
	ChatParticipantRole
} from '@models/chat/chat.model'
import { ChatRoomService } from '@services/chat-room.service'
import { ChatRoomSession, ChatSocketService } from '@services/socket/chat-socket.service'
import { ToastService } from '@common/services'
import { HttpParams } from '@angular/common/http'
import { finalize, takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { NoticeSessionChatFloatingService, NoticeSessionFloatingPosition, NoticeSessionFloatingState } from './notice-session-chat-floating.service'
import { RealtimeFallbackPoller } from '@services/realtime/realtime-fallback-poller'
import { RealtimeNoticeLevel, RealtimeStatusNotice } from '@services/realtime/realtime-status-notice'
import { VisitorSessionService } from '@services/visitor-session.service'

type RealtimeConnectionStatus = 'connecting' | 'connected' | 'disconnected'

/** Intervalo da reverificação enquanto a sala do chat ainda não existe. */
const AWAITING_ROOM_RETRY_MS = 15_000

@Component({
	selector: 'app-notice-session-chat',
	templateUrl: './notice-session-chat.component.html',
	styleUrls: ['./notice-session-chat.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class NoticeSessionChatComponent implements OnChanges, OnDestroy, AfterViewInit, OnInit {
	@ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>
	@ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>

	@Input() noticeSession!: NoticeSession
	@Input() inlineContext = true
	/**
	 * Dentro do balão do botão flutuante do agente (`NoticeSessionChatLauncherComponent`, 27/09/2026):
	 * o balão já flutua, então o modo flutuante próprio — e a preferência gravada dele, que poria o
	 * chat por cima do balão — fica desligado; o cabeçalho ganha "Fechar" e a altura acompanha o balão.
	 */
	@Input() balloon = false
	/**
	 * Visitante (spec 048, RF 29): o chat em leitura. Não procura nem cria sala, lê fases e mensagens de `/visitors/**`,
	 * escuta só o espelho `visitor-chat` da sala e não tem compositor nem presença: o visitante não aparece na sala.
	 */
	@Input() viewerMode = false
	/** Sala do leitor, que `processSession` devolve só depois da abertura. Sem ela, o chat espera a sessão abrir. */
	@Input() readerRoomId: string | null = null
	/** "Fechar" do cabeçalho no modo balão — quem fecha é o dono do balão. */
	@Output() closed = new EventEmitter<void>()
	/**
	 * Mensagem nova que chegou pelo tempo real e não é do próprio usuário (29/09/2026): o balão conta as
	 * que entram enquanto está fechado, para o badge do botão.
	 */
	@Output() messageReceived = new EventEmitter<ChatMessage>()

	/** Leitura também com o produto VIS ativo, mesmo que alguém monte o chat sem `viewerMode` (defesa em profundidade). */
	get readonlyMode(): boolean {
		return this.viewerMode || !!this.accountService?.currentProductVisitor
	}

	readonly messageForm = this.fb.group({
		message: ['', [Validators.required, Validators.maxLength(2000)]]
	})

	room?: ChatRoom
	messages: ChatMessage[] = []
	ensuringRoom = false
	messagesLoading = false
	messagesLoadingMore = false
	sendingMessage = false
	floating = false
	minimized = false
	floatingPosition?: NoticeSessionFloatingPosition
	dragEnabled = false
	realtimeUnstable = false
	/** Nível do aviso de tempo real — o texto e a cor saem daqui. */
	realtimeNotice: RealtimeNoticeLevel = 'none'

	/**
	 * Filtro por FASE DE LANCES (spec 034): presente só quando a sessão tem fases (disputa) — a
	 * dispensa segue sem o filtro. Mensagens novas carregam o vínculo da fase (metadata PHASE_ID) e,
	 * do lado do servidor, o remetente participante ganha um apelido PRÓPRIO por fase; mensagens de
	 * disputas anteriores (sem o vínculo) não são afetadas e só aparecem na visão "Todas".
	 */
	phases: NoticeSessionPhaseBid[] = []
	phaseFilterId: string | null = null

	private currentSessionId?: string
	private roomSession?: ChatRoomSession
	private roomSessionStatus: RealtimeConnectionStatus = 'disconnected'
	private everFullyConnected = false
	private reconnectResyncHandle?: ReturnType<typeof setTimeout>
	private chatFallbackPoller?: RealtimeFallbackPoller
	/**
	 * Há uma queda para recuperar por REST — o que o aviso "sincronizando" comunica.
	 *
	 * A primeira versão decidia isso pelo SILÊNCIO do canal (tempo desde o último frame), e estava
	 * errada: silêncio é o estado NORMAL de um canal de eventos esporádicos. Numa sala sem mensagem
	 * nova por meio minuto a tela passava a dizer "Sincronizando com o servidor..." e não saía mais
	 * disso — visto em staging em 23/09/2026, depois de um rollout do broker. Desde 24/09/2026 nem
	 * o poller usa o silêncio: a régua dele é a CONEXÃO (ver `startChatFallback`).
	 */
	private resyncPending = false

	private readonly notice = new RealtimeStatusNotice({
		onChange: level => {
			this.realtimeNotice = level
			this.realtimeUnstable = level !== 'none'
			this.syncComposerState()
			this.cdr.markForCheck()
		}
	})
	private awaitingRoomHandle?: ReturnType<typeof setTimeout>
	private destroy$ = new Subject<void>()
	private typingTimeout?: ReturnType<typeof setTimeout>
	private heartbeatHandle?: ReturnType<typeof setInterval>
	private roomSessionDestroy$ = new Subject<void>()
	private readonly typingMap = new Map<string, number>()
	private readonly presence = new Map<string, ChatPresence>()
	/** Só a busca de presença mais recente vale; os eventos que chegam enquanto ela está em voo ficam por cima dela. */
	private presenceLoadSeq = 0
	private presenceSinceLoad?: Map<string, ChatPresence>
	private readonly composerMinHeight = 44
	private readonly composerMaxHeight = 132
	private readonly floatingStorageKey = 'notice-session-chat-floating'
	private readonly messagesPageSize = 50
	private dragPointerId?: number
	private dragOffset = { x: 0, y: 0 }
	private isDragging = false
	private dragHandle?: HTMLElement
	private messagesPage = 0
	private messagesLast = true
	private messagesRequesting = false

	constructor(
		private fb: FormBuilder,
		private toast: ToastService,
		private cdr: ChangeDetectorRef,
		private accountService: AccountService,
		private chatRoomService: ChatRoomService,
		private hostElement: ElementRef<HTMLElement>,
		private chatSocketService: ChatSocketService,
		private phaseBidService: NoticeSessionPhaseBidService,
		private floatingService: NoticeSessionChatFloatingService,
		// Opcional e no fim: as specs instanciam o componente com o construtor posicional, fora do contexto de injeção.
		private visitorSessionService?: VisitorSessionService
	) {}

	ngOnInit(): void {
		if (this.balloon) {
			this.floating = false
			return
		}

		this.floating = this.inlineContext ? this.readFloatingPreference() : true
		this.floatingService.state$.pipe(takeUntil(this.destroy$)).subscribe(state => this.handleFloatingState(state))
		this.handleFloatingState(this.floatingService.snapshot)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['noticeSession'] || (this.readonlyMode && changes['readerRoomId'])) {
			const session = this.noticeSession
			if (session?.id) {
				this.initializeForSession(session)
			} else {
				this.detachRoomSession()
				this.room = undefined
				this.messages = []
				this.clearPresence()
				this.typingMap.clear()
				this.cdr.markForCheck()
			}
		}

		if (changes['noticeSession'] && !this.balloon) {
			this.applyFloatingStateFromService()
		}
	}

	ngAfterViewInit(): void {
		this.resetComposerHeight()
	}

	ngOnDestroy(): void {
		this.notice.dispose()
		this.stopDrag()
		this.stopAwaitingRoom()
		if (!this.balloon) {
			this.detachInlineFloating()
		}
		this.detachRoomSession()
		this.destroy$.next()
		this.destroy$.complete()
	}

	refreshRoom(): void {
		if (this.noticeSession?.id) {
			this.initializeForSession(this.noticeSession)
		}
	}

	sendMessage(): void {
		if (this.readonlyMode) return
		if (!this.room || this.messageForm.invalid || this.sendingMessage) return

		const content = (this.messageForm.value.message || '').trim()
		if (!content) return

		const role = this.accountService.currentProductSupplier ? ChatParticipantRole.SUPPLIER : ChatParticipantRole.OWNER

		const payload: ChatMessagePayload = {
			role,
			content,
			type: ChatMessageType.TEXT
		}

		const phaseId = this.resolveOutgoingPhaseId()

		if (phaseId) {
			payload.metadata = { data: { PHASE_ID: phaseId } }
		}

		this.sendingMessage = true

		this.chatRoomService
			.sendMessage(this.room.id, this.room.tenantId, payload)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.sendingMessage = false
					this.cdr.markForCheck()
				})
			)
			.subscribe({
				next: () => {
					this.messageForm.reset()
					this.resetComposerHeight()
					this.roomSession?.sendTyping(false)
				}
			})
	}

	handleComposerInput(event: Event): void {
		this.notifyTyping()
		this.autoResizeTextarea(event.target as HTMLTextAreaElement)
	}

	handleComposerKeydown(event: KeyboardEvent): void {
		if (event.key !== 'Enter') return
		const target = event.target as HTMLTextAreaElement | null
		if (event.ctrlKey || event.metaKey) {
			event.preventDefault()
			if (target) {
				this.insertLineBreak(target)
			}
			return
		}
		event.preventDefault()
		this.sendMessage()
	}

	handleComposerFocus(): void {
		this.notifyActive()
		this.autoResizeTextarea()
	}

	handleComposerBlur(): void {
		this.notifyIdle()
	}

	toggleFloatingMode(): void {
		this.floating = !this.floating
		if (this.inlineContext) {
			this.persistFloatingPreference(this.floating)
		}

		if (!this.noticeSession) {
			if (!this.floating) {
				this.floatingService.disableFloating()
			}
			this.minimized = false
			this.floatingPosition = undefined
			this.cdr.markForCheck()
			return
		}

		if (this.floating) {
			if (!this.inlineContext) {
				this.minimized = false
			}
			this.floatingService.enableFloating(this.noticeSession, {
				inlineAttached: this.inlineContext,
				minimized: this.minimized,
				position: this.floatingPosition,
				viewerMode: this.readonlyMode,
				readerRoomId: this.readerRoomId
			})
		} else {
			this.minimized = false
			this.floatingPosition = undefined
			this.floatingService.disableFloating()
		}
		this.cdr.markForCheck()
	}

	toggleMinimize(event?: Event): void {
		event?.stopPropagation()
		if (!this.floating) return
		this.minimized = !this.minimized
		this.floatingService.setMinimized(this.minimized)
		this.cdr.markForCheck()
	}

	closeFloatingChat(event?: Event): void {
		event?.stopPropagation()
		if (!this.floating) return
		this.minimized = false
		this.floatingPosition = undefined
		this.floatingService.disableFloating()
		if (this.inlineContext) {
			this.floating = false
			this.persistFloatingPreference(false)
		}
		this.cdr.markForCheck()
	}

	requestClose(event?: Event): void {
		event?.stopPropagation()
		this.closed.emit()
	}

	onHeaderPointerDown(event: PointerEvent): void {
		if (!this.dragEnabled || !this.floating || !event.isPrimary) return
		if (event.button !== 0 && event.pointerType !== 'touch') return
		const target = event.target as HTMLElement
		if (target?.closest('button')) {
			return
		}
		event.preventDefault()
		this.dragHandle = event.currentTarget as HTMLElement
		this.startDrag(event)
	}

	notifyTyping(): void {
		if (this.readonlyMode || !this.roomSession) return
		this.roomSession.sendTyping(true)
		if (this.typingTimeout) clearTimeout(this.typingTimeout)
		this.typingTimeout = setTimeout(() => this.roomSession?.sendTyping(false), 2500)
	}

	notifyActive(): void {
		if (this.readonlyMode) return
		this.roomSession?.heartbeat()
	}

	notifyIdle(): void {
		if (this.readonlyMode) return
		this.roomSession?.sendTyping(false)
	}

	onMessagesScroll(): void {
		if (this.messagesRequesting || this.messagesLast || !this.room || !this.messagesContainer) return

		const el = this.messagesContainer.nativeElement
		const threshold = 120
		const distanceToTop = el.scrollTop

		if (distanceToTop <= threshold) {
			this.loadMessages(this.room.id, this.room.tenantId, false)
		}
	}

	statusClass(status?: ChatRoomStatus): string {
		switch (status) {
			case ChatRoomStatus.CLOSED:
				return 'text-bg-danger'
			case ChatRoomStatus.ARCHIVED:
				return 'text-bg-info'
			case ChatRoomStatus.OPEN:
				return 'text-bg-success'
			default:
				return 'text-bg-light'
		}
	}

	protected get noticeTitle(): string {
		return this.room?.title || this.noticeSession?.externalCode || 'Sessão'
	}

	get floatingStyles(): Record<string, string> | null {
		if (!this.floating || !this.floatingPosition) return null
		return {
			left: `${this.floatingPosition.x}px`,
			top: `${this.floatingPosition.y}px`,
			right: 'auto',
			bottom: 'auto'
		}
	}

	get onlineSummary(): string {
		const total = this.room?.participants?.length ?? 0
		const online = Array.from(this.presence.values()).filter(p => p.status === ChatMemberStatus.ONLINE).length
		return `${online} online • ${total} participantes`
	}

	presenceClass(memberId: string, role: ChatParticipantRole): string {
		if (!memberId || !role) return 'offline'

		const key = `${memberId}:${role}`
		const status = this.presence.get(key)?.status
		return status === ChatMemberStatus.ONLINE ? 'online' : 'offline'
	}

	isTyping(memberId?: string | null): boolean {
		if (!memberId) return false
		const expires = this.typingMap.get(memberId)
		return !!expires && expires > Date.now()
	}

	orderParticipants(participants: ChatParticipant[]): ChatParticipant[] {
		return participants?.sort((a, b) => a.role - b.role)
	}

	handlerParticipantName(participant: ChatParticipant, initials = false): string {
		if (participant.member.role === ChatParticipantRole.OWNER) return 'Agente'

		const nickname = participant.member?.useNickName
		const name = (nickname ? participant.member?.nickName : participant.member?.name) ?? 'Participante'

		if (nickname) return name

		return initials ? this.initials(name) : name
	}

	initials(name?: string): string {
		if (!name) return 'Participante'
		return name
			.split(' ')
			.slice(0, 2)
			.map(part => part.charAt(0))
			.join('')
			.toUpperCase()
	}

	private initializeForSession(session: NoticeSession): void {
		this.currentSessionId = session.id
		this.detachRoomSession()
		this.room = undefined
		this.messages = []
		this.clearPresence()
		this.typingMap.clear()
		this.minimized = false
		this.floatingPosition = undefined
		this.phases = []
		this.phaseFilterId = null
		this.cdr.markForCheck()

		this.loadSessionPhases(session.id, session.tenantId)

		if (this.readonlyMode) {
			if (this.readerRoomId) this.handleReaderRoomReady(this.readerRoomId, session.tenantId)
			return
		}

		this.ensureRoom(session)
	}

	/** Sala do visitante: sem participantes, presença nem heartbeat, só as mensagens e o espelho do socket. */
	private handleReaderRoomReady(roomId: string, tenantId: string): void {
		this.room = new ChatRoom({ id: roomId, tenantId } as Partial<ChatRoom>)
		this.messages = []
		this.messagesPage = 0
		this.messagesLast = true
		this.messagesRequesting = false
		this.cdr.markForCheck()

		this.loadMessages(roomId, tenantId, true)
		this.attachRoomSession(roomId, tenantId)
	}

	/**
	 * Fases da sessão para o filtro do chat — sessão sem fase (dispensa) fica sem o filtro.
	 *
	 * `tenantId` vai na query porque o chat também roda na tela do FORNECEDOR, onde o
	 * `ClientInterceptor` não injeta `x-tenant-id` (produto SUP): sem ele o servidor recusa com
	 * 400 e o participante ficava sem o filtro por fase de lances (spec 034).
	 */
	private loadSessionPhases(sessionId: string, tenantId?: string): void {
		if (this.readonlyMode) {
			this.visitorSessionService
				?.phases(sessionId)
				.pipe(takeUntil(this.destroy$))
				.subscribe(phases => {
					if (this.currentSessionId !== sessionId) return
					this.phases = (phases ?? []).filter(phase => !!phase?.id)
					this.cdr.markForCheck()
				})
			return
		}

		let params = new HttpParams().set('size', '100')

		if (tenantId) {
			params = params.set('tenantId', tenantId)
		}

		this.phaseBidService
			.search(new NoticeSessionPhaseBidSearch({ sessionId }), params)
			.pipe(takeUntil(this.destroy$))
			.subscribe(response => {
				if (this.currentSessionId !== sessionId) return
				this.phases = (response?.content ?? []).filter(phase => !!phase?.id)
				this.cdr.markForCheck()
			})
	}

	onPhaseFilterChange(phaseId: string): void {
		this.phaseFilterId = phaseId || null

		if (this.room) {
			this.messages = []
			this.messagesPage = 0
			this.messagesLast = true
			this.loadMessages(this.room.id, this.room.tenantId, true)
		}
	}

	/**
	 * Fase vinculada à mensagem que sai agora: a do filtro selecionado; sem filtro, a fase ABERTA
	 * mais recente da sessão (mensagens trocadas durante uma disputa pertencem a ela); sem fase
	 * aberta, a mensagem sai sem vínculo — comportamento das disputas anteriores e da dispensa.
	 */
	private resolveOutgoingPhaseId(): string | null {
		if (this.phaseFilterId) return this.phaseFilterId

		const opened = this.phases.filter(phase => phase.statusOpened)
		if (!opened.length) return null

		return [...opened].sort((a, b) => (b.created ?? '').localeCompare(a.created ?? ''))[0]?.id ?? null
	}

	private ensureRoom(session: NoticeSession): void {
		this.ensuringRoom = true
		let params = new HttpParams()
		params = params.set('noticeSessionId', session.id)
		params = params.set('size', '1')
		params = params.set('sort', 'updatedAt,desc')

		this.chatRoomService
			.list(session.tenantId, params)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.ensuringRoom = false
					this.cdr.markForCheck()
				})
			)
			.subscribe({
				next: response => {
					if (this.currentSessionId !== session.id) {
						return
					}
					const room = response?.content?.[0]
					if (room) {
						this.stopAwaitingRoom()
						this.handleRoomReady(room)
						return
					}

					// Sem sala, a busca terminava aqui e a tela ficava parada em "Abra a sessão que a
					// sala será habilitada" para sempre. A sala nasce quando o AGENTE abre a sessão, e o
					// participante não recebe notificação desse ato — o socket do fornecedor não carrega
					// abertura de sessão, e o de fase só existe depois que uma fase é aberta. Sem uma
					// nova verificação, só recarregando a página o chat destravava.
					this.scheduleAwaitingRoomRetry(session)
				},
				error: () => this.toast.error('Falha ao localizar a sala do chat')
			})
	}

	/**
	 * Reverifica a existência da sala enquanto ela não existir.
	 *
	 * Só roda neste estado de espera e para no instante em que a sala aparece — não é sondagem do
	 * chat, é a saída de um estado que não tem nenhum evento para observar. O intervalo é largo de
	 * propósito: quem está esperando a sessão abrir tolera alguns segundos, e a sala é criada uma vez.
	 */
	private scheduleAwaitingRoomRetry(session: NoticeSession): void {
		this.stopAwaitingRoom()

		// Sessão encerrada não vai ganhar sala; insistir seria pedido sem destino.
		if (this.noticeSession?.statusClosed) return

		this.awaitingRoomHandle = setTimeout(() => {
			this.awaitingRoomHandle = undefined

			if (this.currentSessionId !== session.id || this.room) return

			this.ensureRoom(session)
		}, AWAITING_ROOM_RETRY_MS)
	}

	private stopAwaitingRoom(): void {
		if (this.awaitingRoomHandle) {
			clearTimeout(this.awaitingRoomHandle)
			this.awaitingRoomHandle = undefined
		}
	}

	private createRoomForSession(session: NoticeSession): void {
		const payload = {
			noticeSessionId: session.id,
			title: session.externalCode ? `${session.externalCode}` : undefined
		}

		this.chatRoomService
			.create(payload, this.accountService.tenantId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: room => {
					if (this.currentSessionId !== session.id) {
						return
					}
					this.handleRoomReady(room)
				},
				error: () => this.toast.error('Não foi possível criar a sala para esta sessão')
			})
	}

	private handleRoomReady(room: ChatRoom): void {
		this.room = new ChatRoom(room)
		this.messages = []
		this.messagesPage = 0
		this.messagesLast = true
		this.messagesRequesting = false
		this.clearPresence()
		this.typingMap.clear()
		this.cdr.markForCheck()

		// A presença vem na primeira conexão da sala (`onRealtimeStatusChange`), com o tópico já assinado.
		this.loadMessages(room.id, room.tenantId, true)
		this.attachRoomSession(room.id, room.tenantId)
	}

	private loadMessages(roomId: string, tenantId: string, reset = false): void {
		if (this.messagesRequesting) return
		if (!reset && this.messagesLast) return

		const page = reset ? 0 : this.messagesPage + 1
		const previousScrollHeight = !reset ? (this.messagesContainer?.nativeElement.scrollHeight ?? 0) : 0
		const previousScrollTop = !reset ? (this.messagesContainer?.nativeElement.scrollTop ?? 0) : 0
		this.messagesRequesting = true
		this.messagesLoading = reset
		this.messagesLoadingMore = !reset
		let params = new HttpParams().set('size', `${this.messagesPageSize}`).set('page', `${page}`).set('sort', 'createdAt,desc')

		if (this.phaseFilterId) {
			params = params.set('phaseId', this.phaseFilterId)
		}

		const source =
			this.readonlyMode && this.visitorSessionService
				? this.visitorSessionService.messages(this.currentSessionId, this.phaseFilterId, page, this.messagesPageSize)
				: this.chatRoomService.messages(roomId, tenantId, params)

		source
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.messagesRequesting = false
					this.messagesLoading = false
					this.messagesLoadingMore = false
					if (reset) {
						this.scrollMessagesToBottom('auto')
					}
					this.cdr.markForCheck()
				})
			)
			.subscribe({
				next: response => {
					const content = (response?.content ?? []).map(it => new ChatMessage(it))

					if (reset) {
						this.messages = content.sort((a, b) => a.created.localeCompare(b.created))
					} else {
						const existing = new Set(this.messages.map(it => it.id))
						const incoming = content.filter(it => !existing.has(it.id))
						const incomingOrdered = incoming.sort((a, b) => a.created.localeCompare(b.created))
						this.messages = [...incomingOrdered, ...this.messages]
						this.restoreScrollAfterPrepend(previousScrollHeight, previousScrollTop)
					}

					this.messagesPage = response?.number ?? page
					const responseLast = response?.last
					this.messagesLast = typeof responseLast === 'boolean' ? responseLast : content.length < this.messagesPageSize
				},
				error: () => this.toast.error('Erro ao carregar mensagens')
			})
	}

	private restoreScrollAfterPrepend(previousScrollHeight: number, previousScrollTop: number): void {
		setTimeout(() => {
			const el = this.messagesContainer?.nativeElement
			if (!el) return
			const heightDelta = el.scrollHeight - previousScrollHeight
			el.scrollTop = previousScrollTop + Math.max(heightDelta, 0)
		}, 0)
	}

	/**
	 * Snapshot da presença por REST. O servidor publica a presença só quando ela muda (28/10/2026), e não mais a cada
	 * heartbeat. Por isso um evento que chega com a busca em voo (a chegada da própria aba, por exemplo) pode ser mais
	 * novo que o snapshot e fica por cima dele. Antes a resposta apagava esse evento, e o heartbeat seguinte corrigia
	 * em 20 s. Agora nada corrigiria.
	 */
	private loadPresence(roomId: string, tenantId: string): void {
		if (this.readonlyMode) return

		const seq = ++this.presenceLoadSeq
		const sinceLoad = new Map<string, ChatPresence>()
		this.presenceSinceLoad = sinceLoad

		this.chatRoomService
			.presence(roomId, tenantId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: snapshot => {
					if (seq !== this.presenceLoadSeq) return

					this.presenceSinceLoad = undefined
					this.presence.clear()

					snapshot?.forEach(entry => {
						if (entry.key) {
							this.presence.set(entry.key, entry)
						}
					})
					sinceLoad.forEach((entry, key) => this.presence.set(key, entry))

					this.cdr.markForCheck()
				},
				error: () => {
					if (seq === this.presenceLoadSeq) this.presenceSinceLoad = undefined
				}
			})
	}

	private clearPresence(): void {
		this.presence.clear()
		// Um snapshot ainda em voo é da sala anterior.
		this.presenceLoadSeq++
		this.presenceSinceLoad = undefined
	}

	private attachRoomSession(roomId: string, tenantId: string): void {
		this.roomSessionDestroy$.next()
		this.roomSessionDestroy$.complete()
		this.roomSessionDestroy$ = new Subject<void>()

		this.roomSessionStatus = 'disconnected'
		this.everFullyConnected = false
		// A sala nova carrega tudo por REST (`handleRoomReady`): não herda o re-sync pendente da anterior. Herdado, ele
		// ficava sem quem o encerrasse — a primeira conexão da sala não agenda re-sync — e o aviso "sincronizando" não
		// saía mais até chegar uma mensagem (27/10/2026).
		this.resyncPending = false
		this.notice.reset()

		this.roomSession = this.readonlyMode
			? this.chatSocketService.createReaderSession(roomId, tenantId)
			: this.chatSocketService.createRoomSession(roomId, tenantId, this.currentMemberRole)

		this.roomSession.events$.pipe(takeUntil(this.roomSessionDestroy$)).subscribe(event => this.handleEvent(event))
		this.roomSession.typing$.pipe(takeUntil(this.roomSessionDestroy$)).subscribe(payload => this.registerTyping(payload))
		this.roomSession.presence$.pipe(takeUntil(this.roomSessionDestroy$)).subscribe(payload => this.updatePresence(payload))
		this.roomSession.status$.pipe(takeUntil(this.roomSessionDestroy$)).subscribe(status => {
			this.roomSessionStatus = status
			this.onRealtimeStatusChange()
		})

		if (!this.readonlyMode) {
			this.roomSession.heartbeat()
			this.heartbeatHandle = setInterval(() => this.roomSession?.heartbeat(), 20000)
		}
		this.startChatFallback()
	}

	private detachRoomSession(): void {
		if (this.roomSession) {
			this.roomSession.disconnect()
			this.roomSession = undefined
		}

		if (this.heartbeatHandle) {
			clearInterval(this.heartbeatHandle)
			this.heartbeatHandle = undefined
		}

		if (this.reconnectResyncHandle) {
			clearTimeout(this.reconnectResyncHandle)
			this.reconnectResyncHandle = undefined
		}

		this.stopChatFallback()

		this.roomSessionStatus = 'disconnected'
		this.everFullyConnected = false
		this.resyncPending = false
		this.notice.reset()

		this.roomSessionDestroy$.next()
		this.roomSessionDestroy$.complete()
		this.roomSessionDestroy$ = new Subject<void>()
	}

	private onRealtimeStatusChange(): void {
		const isConnected = this.roomSessionStatus === 'connected'
		this.refreshRealtimeNotice()

		if (!isConnected) return

		if (!this.everFullyConnected) {
			this.everFullyConnected = true
			// Só agora, com a sala assinada: quem chegasse entre um snapshot tirado antes e a assinatura não apareceria.
			if (this.room) this.loadPresence(this.room.id, this.room.tenantId)
			return
		}

		this.scheduleReconnectResync()
	}

	/**
	 * Classifica o tempo real em três estados e deixa a histerese para o RealtimeStatusNotice.
	 *
	 * O terceiro estado é o que faltava: socket CONECTADO e sem receber frame nenhum. Era o caso
	 * mais comum (broker por pod, e depois destino recusado pelo relay) e a tela anunciava
	 * "reconectando", que é falso — a conexão está de pé, quem serve a sala é o poller REST.
	 */
	private refreshRealtimeNotice(): void {
		if (this.roomSessionStatus !== 'connected') {
			// Reconectar depois desta queda implica buscar por REST o que passou fora do ar.
			if (this.everFullyConnected) this.resyncPending = true
			this.notice.update('offline')
			this.syncComposerState()
			return
		}

		this.notice.update(this.resyncPending ? 'syncing' : 'ok')
		this.syncComposerState()
	}

	/**
	 * O bloqueio do compositor segue a CONEXÃO, não o aviso na tela.
	 *
	 * Eram a mesma coisa até 23/09/2026, e com a histerese do aviso deixaram de poder ser: o
	 * rótulo agora permanece alguns segundos depois de a conexão voltar (para não piscar), e
	 * manter o campo travado nesse intervalo puniria o usuário por uma instabilidade que já passou.
	 *
	 * Trava só quando a queda foi anunciada E ainda está de pé — na carência o campo segue
	 * utilizável, e em `syncing` também: ali a conexão existe, o que falta são frames, e o envio é
	 * REST de qualquer forma.
	 */
	private syncComposerState(): void {
		const travar = this.notice.offline && this.roomSessionStatus !== 'connected'

		if (travar) this.messageForm.disable()
		else this.messageForm.enable()
	}

	/**
	 * Reconciliação pós-reconexão: STOMP já re-inscreve os tópicos em onConnect, mas eventos
	 * ocorridos durante a queda (mensagens, presença) são perdidos. Recarrega mensagens (reset)
	 * e presença via REST. Debounce evita múltiplos resyncs em flap de conexão.
	 */
	private scheduleReconnectResync(): void {
		if (this.reconnectResyncHandle) {
			clearTimeout(this.reconnectResyncHandle)
		}

		this.reconnectResyncHandle = setTimeout(() => {
			this.reconnectResyncHandle = undefined
			this.resyncFromServer()
		}, 1500)
	}

	/** Recarrega do servidor o que o socket deveria ter entregue. */
	private resyncFromServer(): void {
		// Buscar do servidor É a sincronização que o aviso anuncia: uma vez disparada, o aviso
		// cumpriu o papel.
		this.resyncPending = false
		this.refreshRealtimeNotice()

		try {
			if (!this.room) return
			this.loadMessages(this.room.id, this.room.tenantId, true)
			this.loadPresence(this.room.id, this.room.tenantId)
		} finally {
			this.chatFallbackPoller?.settle()
		}
	}

	/**
	 * Polling de escape (2026-09-23; régua trocada em 24/09/2026).
	 *
	 * Só roda com o socket da sala FORA. O WS é o canal primário: conectado, o broker (relay para o
	 * RabbitMQ) entrega tudo, e buscar `messages` + `presence` em ciclo era só carga — o consumo
	 * indevido relatado na tela do participante. O envio é REST, então o que se perde numa queda é
	 * LEITURA, e é o que o poller recupera até a reconexão (`docs/realtime-stomp.md`).
	 */
	private startChatFallback(): void {
		if (this.chatFallbackPoller) return

		this.chatFallbackPoller = new RealtimeFallbackPoller({
			resync: () => this.resyncFromServer(),
			isConnected: () => this.roomSessionStatus === 'connected'
		})

		this.chatFallbackPoller.start()
	}

	private stopChatFallback(): void {
		this.chatFallbackPoller?.stop()
		this.chatFallbackPoller = undefined
	}

	private handleEvent(event: ChatWsEvent): void {
		if (!event || !this.room) return

		// Chegou frame: o canal entrega de novo, e o aviso perde a razão mesmo sem o re-sync.
		this.resyncPending = false
		this.refreshRealtimeNotice()

		switch (event.type) {
			case 'MESSAGE_CREATED':
				this.pushMessage(new ChatMessage(event.payload))
				break
			case 'ROOM_UPDATED':
			case 'ROOM_STATUS_CHANGED':
				this.applyRoomUpdate(new ChatRoom(event.payload))
				break
			case 'PARTICIPANT_ADDED':
				this.mergeParticipants(Array.isArray(event.payload) ? event.payload : [event.payload])
				break
			case 'PARTICIPANT_LEFT':
				this.markParticipantDeparture(event.payload)
				break
			default:
				break
		}
	}

	private pushMessage(message: ChatMessage): void {
		if (!message || message.chatRoomId !== this.room?.id) return
		// O filtro de fase recorta apenas EVENTO DE SISTEMA de outra fase (correção de 2026-09-23).
		// Antes qualquer mensagem sem o vínculo da fase selecionada era descartada aqui — inclusive a
		// que uma pessoa acabara de escrever: ela chegava pelo socket e era jogada fora, e quem
		// estava filtrando achava que a mensagem não tinha chegado.
		const messagePhaseId = message.metadata?.['data']?.['PHASE_ID']
		if (this.phaseFilterId && message.system && messagePhaseId && messagePhaseId !== this.phaseFilterId) return
		const exists = this.messages.some(it => it.id === message.id)
		if (!exists) {
			this.messages = [...this.messages, message]

			if (!this.isOwnMessage(message)) {
				this.messageReceived.emit(message)
			}
		}
		this.messages = this.messages.sort((a, b) => a.created.localeCompare(b.created))
		this.cdr.markForCheck()
		this.scrollMessagesToBottom()
	}

	private applyRoomUpdate(room: ChatRoom): void {
		if (!room || room.id !== this.room?.id) return
		this.room = new ChatRoom({ ...this.room, ...room })
		this.cdr.markForCheck()
	}

	private mergeParticipants(participants: any[]): void {
		if (!this.room) return

		const existing = this.room.participants ?? []
		const merged = [...existing]

		participants.forEach(participant => {
			const index = merged.findIndex(p => p.id === participant.id)
			if (index >= 0) {
				merged[index] = participant
			} else {
				merged.push(participant)
			}
		})

		this.room = new ChatRoom({ ...this.room, participants: merged })
		this.cdr.markForCheck()
	}

	private markParticipantDeparture(participant: any): void {
		if (!this.room?.participants) return

		this.room = new ChatRoom({
			...this.room,
			participants: this.room.participants.map(p => (p.id === participant.id ? { ...p, leftAt: participant.leftAt } : p))
		})

		const key = `${participant.memberId}:${participant.role}`
		this.presence.delete(key)
		this.presenceSinceLoad?.delete(key)
		this.cdr.markForCheck()
	}

	private registerTyping(payload: { memberId: string; typing: boolean }): void {
		if (!payload) return

		const selfId = this.currentMemberId

		if (selfId && payload.memberId === selfId) {
			return
		}

		if (payload.typing) {
			const expiration = Date.now() + 5000
			this.typingMap.set(payload.memberId, expiration)
			setTimeout(() => this.clearTyping(payload.memberId, expiration), 5200)
		} else {
			this.typingMap.delete(payload.memberId)
		}

		this.cdr.markForCheck()
	}

	private clearTyping(memberId: string, expected: number): void {
		const current = this.typingMap.get(memberId)

		if (current && current <= expected) {
			this.typingMap.delete(memberId)
			this.cdr.markForCheck()
		}
	}

	private updatePresence(payload: ChatPresence): void {
		if (!payload) return

		const presence = new ChatPresence(payload)

		if (presence.key) {
			this.presence.set(presence.key, presence)
			this.presenceSinceLoad?.set(presence.key, presence)
		}

		this.cdr.markForCheck()
	}

	private scrollMessagesToBottom(behavior: ScrollBehavior = 'smooth'): void {
		setTimeout(() => {
			if (!this.messagesContainer) return
			const el = this.messagesContainer.nativeElement
			el.scrollTo({ top: el.scrollHeight, behavior })
		}, 0)
	}

	private isOwnMessage(message: ChatMessage): boolean {
		const memberId = this.currentMemberId
		const userId = this.accountService.user?.id
		const senderMemberId = message.sender?.id || message.sender?.userId || message.senderId

		if (memberId && senderMemberId && senderMemberId === memberId) {
			return true
		}

		return userId && (message.sender?.userId === userId || senderMemberId === userId)
	}

	get currentMemberRole(): ChatParticipantRole {
		// O visitante nunca fala como órgão: com o produto VIS, o papel é o do lado de fora da mesa.
		return this.accountService.currentProductSupplier || this.readonlyMode ? ChatParticipantRole.SUPPLIER : ChatParticipantRole.OWNER
	}

	private get currentMemberId(): string | undefined {
		const userId = this.accountService.user?.id
		if (!userId) return undefined
		const participant = this.room?.participants?.find(
			member => (member.member?.userId === userId || member.memberId === userId || member.member?.id === userId) && member.role === this.currentMemberRole
		)
		return participant?.memberId ?? participant?.member?.id
	}

	@HostListener('window:pointermove', ['$event'])
	onPointerMove(event: PointerEvent): void {
		if (!this.dragEnabled) return
		if (!this.isDragging || event.pointerId !== this.dragPointerId) return
		event.preventDefault()
		this.updateFloatingPosition(
			{
				x: event.clientX - this.dragOffset.x,
				y: event.clientY - this.dragOffset.y
			},
			true
		)
	}

	@HostListener('window:pointerup', ['$event'])
	onPointerUp(event: PointerEvent): void {
		if (!this.dragEnabled) return
		if (event.pointerId !== this.dragPointerId) return
		this.stopDrag()
	}

	@HostListener('window:pointercancel')
	onPointerCancel(): void {
		if (!this.dragEnabled) return
		this.stopDrag()
	}

	@HostListener('window:blur')
	onWindowBlur(): void {
		if (!this.dragEnabled) return
		this.stopDrag()
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		if (!this.dragEnabled) return
		if (!this.floatingPosition) return
		const current = { ...this.floatingPosition }
		this.updateFloatingPosition(current, true)
	}

	private startDrag(event: PointerEvent): void {
		if (!this.dragEnabled) return
		if (!this.floating) return
		const position = this.ensureFloatingPosition()
		this.dragPointerId = event.pointerId
		this.dragOffset = {
			x: event.clientX - position.x,
			y: event.clientY - position.y
		}
		this.isDragging = true
		this.dragHandle?.setPointerCapture?.(event.pointerId)
	}

	private stopDrag(): void {
		if (!this.dragEnabled) return
		if (!this.isDragging) return
		this.isDragging = false
		if (this.dragPointerId !== undefined) {
			try {
				this.dragHandle?.releasePointerCapture?.(this.dragPointerId)
			} catch {
				// ignore release errors
			}
		}
		this.dragPointerId = undefined
		this.dragHandle = undefined
	}

	private ensureFloatingPosition(): NoticeSessionFloatingPosition {
		if (this.floatingPosition) {
			return this.floatingPosition
		}
		const rect = this.hostElement.nativeElement.getBoundingClientRect()
		const position = { x: rect.left, y: rect.top }
		this.updateFloatingPosition(position, false)
		return position
	}

	private updateFloatingPosition(position: NoticeSessionFloatingPosition, persist = true): void {
		const clamped = this.clampPosition(position)
		this.floatingPosition = clamped
		if (persist) {
			this.floatingService.setPosition(clamped)
		}
		this.cdr.markForCheck()
	}

	private clampPosition(position: NoticeSessionFloatingPosition): NoticeSessionFloatingPosition {
		if (typeof window === 'undefined') return position
		const host = this.hostElement.nativeElement
		const width = host.offsetWidth || 320
		const height = host.offsetHeight || 200
		const margin = 12
		const maxX = Math.max(margin, window.innerWidth - width - margin)
		const maxY = Math.max(margin, window.innerHeight - height - margin)
		return {
			x: Math.min(Math.max(margin, position.x), maxX),
			y: Math.min(Math.max(margin, position.y), maxY)
		}
	}

	private autoResizeTextarea(target?: HTMLTextAreaElement): void {
		const textarea = target ?? this.messageInput?.nativeElement
		if (!textarea) return
		textarea.style.height = 'auto'
		const desiredHeight = Math.max(textarea.scrollHeight, this.composerMinHeight)
		const isClamped = desiredHeight > this.composerMaxHeight
		const newHeight = Math.min(desiredHeight, this.composerMaxHeight)
		textarea.style.height = `${newHeight}px`
		textarea.style.overflowY = isClamped ? 'auto' : 'hidden'
		textarea.style.overflowX = 'hidden'
	}

	private resetComposerHeight(): void {
		const textarea = this.messageInput?.nativeElement
		if (!textarea) return
		textarea.style.height = `${this.composerMinHeight}px`
		textarea.style.overflowY = 'hidden'
	}

	private insertLineBreak(target: HTMLTextAreaElement): void {
		const { selectionStart, selectionEnd, value } = target
		const newValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`
		this.messageForm.patchValue({ message: newValue })
		const caret = selectionStart + 1
		target.value = newValue
		target.selectionStart = caret
		target.selectionEnd = caret
		this.notifyTyping()
		this.autoResizeTextarea(target)
	}

	private get safeStorage(): Storage | null {
		try {
			return typeof window !== 'undefined' ? window.localStorage : null
		} catch {
			return null
		}
	}

	private readFloatingPreference(): boolean {
		return this.safeStorage?.getItem(this.floatingStorageKey) === 'true'
	}

	private persistFloatingPreference(value: boolean): void {
		const storage = this.safeStorage
		if (!storage) return
		storage.setItem(this.floatingStorageKey, String(value))
	}

	private applyFloatingStateFromService(): void {
		if (!this.noticeSession) return
		const state = this.floatingService.snapshot
		if (!state || state.noticeSession?.id !== this.noticeSession.id) {
			return
		}
		this.floating = true
		this.handleFloatingState(state)
		if (this.inlineContext) {
			this.floatingService.setInlineAttached(true)
		}
	}

	private detachInlineFloating(): void {
		if (!this.inlineContext || !this.noticeSession) return
		const state = this.floatingService.snapshot
		if (state?.noticeSession?.id === this.noticeSession.id) {
			this.floatingService.setInlineAttached(false)
		}
	}

	private handleFloatingState(state: NoticeSessionFloatingState | null): void {
		if (!state || !this.noticeSession || state.noticeSession?.id !== this.noticeSession.id) {
			if (!state && !this.inlineContext) {
				this.minimized = false
				this.floatingPosition = undefined
				this.cdr.markForCheck()
			}
			return
		}
		this.minimized = !!state.minimized
		this.floatingPosition = state.position
		this.cdr.markForCheck()
	}
}
